
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function Deadlines() {
  const deadlines = [
    {
      item: 'Semester 1 Fee Due',
      deadline: '30 September 2025',
      status: 'upcoming',
      icon: 'ri-calendar-check-line',
      description: 'Final deadline for Semester 1 tuition and administrative fees'
    },
    {
      item: 'Late Fee Begins',
      deadline: '5 October 2025',
      status: 'warning',
      icon: 'ri-alarm-warning-line',
      description: 'Late payment penalties will be applied after this date'
    },
    {
      item: 'Last Day for Payment to Avoid Deregistration',
      deadline: '15 November 2025',
      status: 'critical',
      icon: 'ri-error-warning-line',
      description: 'Final deadline to avoid being removed from courses'
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'upcoming':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getIconColor = (status: string) => {
    switch (status) {
      case 'upcoming':
        return 'bg-green-500';
      case 'warning':
        return 'bg-yellow-500';
      case 'critical':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-gradient-to-r from-orange-600 to-orange-800 text-white py-16"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/191558b5e477c57f66bcd91890798f89.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-orange-900/80"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Fee Payment Deadlines &amp; Important Dates</h1>
          <p className="text-xl max-w-4xl leading-relaxed">
            The following timelines outline the major payment deadlines for the current academic period. Please ensure that fees are paid early to avoid late penalties and registration blocks.
          </p>
        </div>
      </section>

      {/* Deadlines Timeline */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {deadlines.map((deadline, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="flex items-start space-x-6">
                  <div className={`w-16 h-16 ${getIconColor(deadline.status)} rounded-full flex items-center justify-center flex-shrink-0`}>
                    <i className={`${deadline.icon} text-white text-2xl`}></i>
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2 md:mb-0">{deadline.item}</h3>
                      <div className={`inline-flex items-center px-4 py-2 rounded-full border ${getStatusColor(deadline.status)}`}>
                        <i className="ri-calendar-line mr-2"></i>
                        <span className="font-semibold">{deadline.deadline}</span>
                      </div>
                    </div>
                    <p className="text-gray-600 text-lg">{deadline.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Important Reminder */}
      <section className="py-16 bg-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-blue-100 border-l-4 border-blue-500 p-6 rounded-r-lg">
            <div className="flex items-start">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-lightbulb-line text-blue-600 text-xl"></i>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-semibold text-blue-800 mb-2">Payment Recommendation</h3>
                <p className="text-blue-700">
                  Students are strongly encouraged to submit payments <strong>at least 7 days before</strong> the official due date to allow time for bank processing and verification.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Calendar View */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Academic Calendar Overview</h2>
            <p className="text-lg text-gray-600">Key dates for the current semester</p>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">September - November 2025</h3>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-6 bg-green-50 rounded-lg border border-green-200">
                  <div className="text-3xl font-bold text-green-600 mb-2">30</div>
                  <div className="text-sm text-green-700 font-semibold">SEPTEMBER</div>
                  <div className="text-sm text-gray-600 mt-2">Semester 1 Fee Due</div>
                </div>
                
                <div className="text-center p-6 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="text-3xl font-bold text-yellow-600 mb-2">05</div>
                  <div className="text-sm text-yellow-700 font-semibold">OCTOBER</div>
                  <div className="text-sm text-gray-600 mt-2">Late Fee Begins</div>
                </div>
                
                <div className="text-center p-6 bg-red-50 rounded-lg border border-red-200">
                  <div className="text-3xl font-bold text-red-600 mb-2">15</div>
                  <div className="text-sm text-red-700 font-semibold">NOVEMBER</div>
                  <div className="text-sm text-gray-600 mt-2">Final Payment Deadline</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Ready to Make a Payment?</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <a
              href="/how-to-pay"
              className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow cursor-pointer group text-center"
            >
              <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-bank-card-line text-white text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Payment Methods</h3>
              <p className="text-gray-600 text-sm">Learn how to pay your fees</p>
            </a>
            
            <a
              href="/bank-details"
              className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow cursor-pointer group text-center"
            >
              <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-bank-line text-white text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Bank Details</h3>
              <p className="text-gray-600 text-sm">Get account information</p>
            </a>
            
            <a
              href="/contact"
              className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow cursor-pointer group text-center"
            >
              <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-customer-service-line text-white text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Need Help?</h3>
              <p className="text-gray-600 text-sm">Contact student support</p>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
